import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAthlete } from '@/context/AthleteContext';
import { Trophy, Upload, Play, Clock, Flame, Zap, Award, Share2, Download, RefreshCw, X, Info, ChevronRight, Users, AlertCircle, Camera, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import ScoreReveal from '@/components/challenge/ScoreReveal';
import ChallengeRulesModal from '@/components/challenge/ChallengeRulesModal';
import ChallengeUpload from '@/components/challenge/ChallengeUpload';
import RankingTable from '@/components/challenge/RankingTable';
import ShareableCard from '@/components/challenge/ShareableCard';
import VideoShareModal from '@/components/challenge/VideoShareModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCountdown = (endIso) => {
  if (!endIso) return '';
  const end = new Date(endIso).getTime();
  const now = Date.now();
  const diff = end - now;
  if (diff <= 0) return 'Ended';
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  if (days > 0) return `${days}d ${hours}h left`;
  const mins = Math.floor((diff % 3600000) / 60000);
  return `${hours}h ${mins}m left`;
};

const WeeklyChallenge = () => {
  const { getAuthHeaders } = useAuth();
  const { athlete } = useAthlete();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // { challenge, athletes, total_participants }
  const [ranking, setRanking] = useState(null); // { ranking, my_ranks, total_participants }
  const [view, setView] = useState('home'); // home | rules | upload | reveal | ranking | share
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [cardOpen, setCardOpen] = useState(false);
  const [videoShareOpen, setVideoShareOpen] = useState(false);
  const [demoVideoOpen, setDemoVideoOpen] = useState(false);
  const intervalRef = useRef(null);
  const [countdown, setCountdown] = useState('');

  const myAth = data?.athletes?.find(a => a.athlete_id === athlete?.id) || data?.athletes?.[0];

  const fetchActive = async () => {
    try {
      const res = await axios.get(`${API}/challenges/active`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const fetchRanking = async (challengeId) => {
    try {
      const res = await axios.get(`${API}/challenges/${challengeId}/ranking?limit=50`, { headers: getAuthHeaders() });
      setRanking(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    fetchActive();
  }, [athlete?.id]);

  useEffect(() => {
    if (!data?.challenge?.ends_at) return;
    setCountdown(formatCountdown(data.challenge.ends_at));
    intervalRef.current = setInterval(() => {
      setCountdown(formatCountdown(data.challenge.ends_at));
    }, 60000);
    return () => clearInterval(intervalRef.current);
  }, [data?.challenge?.ends_at]);

  useEffect(() => {
    if (view === 'ranking' && data?.challenge?.id) {
      fetchRanking(data.challenge.id);
    }
  }, [view, data?.challenge?.id]);

  const handleSubmit = async ({ videoUrl, brandedVideoUrl, thumbnailUrl, publicId, score }) => {
    if (!myAth) return;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/challenges/attempts/${myAth.athlete_id}`,
        {
          challenge_id: data.challenge.id,
          video_url: videoUrl,
          branded_video_url: brandedVideoUrl,
          thumbnail_url: thumbnailUrl,
          cloudinary_public_id: publicId,
          score,
        },
        { headers: getAuthHeaders() }
      );
      setLastResult(res.data);
      setView('reveal');
      fetchActive();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit attempt');
    }
    setSubmitting(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><RefreshCw className="animate-spin text-primary" size={32} /></div>;
  }

  // ====== NO ACTIVE CHALLENGE ======
  if (!data?.challenge) {
    return (
      <div className="space-y-6" data-testid="weekly-challenge-empty">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight flex items-center gap-3">
            <Trophy className="text-primary" size={32} /> Weekly Challenge
          </h1>
        </div>
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Clock className="mx-auto text-muted-foreground mb-4" size={48} />
          <h3 className="text-lg font-medium mb-2">No active challenge this week</h3>
          <p className="text-muted-foreground text-sm">Come back Monday — a new challenge drops every week.</p>
        </div>
      </div>
    );
  }

  const ch = data.challenge;

  // ====== UPLOAD VIEW ======
  if (view === 'upload') {
    return (
      <ChallengeUpload
        challenge={ch}
        athlete={myAth}
        onSubmit={handleSubmit}
        onCancel={() => setView('home')}
        submitting={submitting}
      />
    );
  }

  // ====== SCORE REVEAL VIEW ======
  if (view === 'reveal' && lastResult) {
    return (
      <ScoreReveal
        result={lastResult}
        challenge={ch}
        athlete={myAth}
        onTryAgain={() => { setLastResult(null); setView('rules'); }}
        onShare={() => setCardOpen(true)}
        onDone={() => setView('home')}
        cardOpen={cardOpen}
        onCardClose={() => setCardOpen(false)}
      />
    );
  }

  // ====== RANKING VIEW ======
  if (view === 'ranking') {
    return (
      <div className="space-y-6" data-testid="weekly-challenge-ranking">
        <div className="flex items-center justify-between">
          <div>
            <button onClick={() => setView('home')} className="text-xs text-muted-foreground hover:text-primary uppercase tracking-widest flex items-center gap-1 mb-2">
              ← Back
            </button>
            <h1 className="font-heading text-3xl uppercase tracking-tight">Weekly Ranking</h1>
            <p className="text-muted-foreground text-sm">{ch.title}</p>
          </div>
        </div>
        <RankingTable ranking={ranking} scoringType={ch.scoring_type} />
      </div>
    );
  }

  // ====== HOME VIEW ======
  return (
    <>
      <div className="space-y-6" data-testid="weekly-challenge-home">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl uppercase tracking-tight flex items-center gap-3">
              <Trophy className="text-primary" size={32} /> Weekly Challenge
            </h1>
            <p className="text-muted-foreground text-sm">{athlete?.name ? `${athlete.name}'s arena` : 'This week\'s challenge'}</p>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 border border-primary/30 rounded-sm">
            <Clock size={14} className="text-primary" />
            <span className="text-xs uppercase tracking-widest text-primary font-bold" data-testid="challenge-countdown">{countdown}</span>
          </div>
        </div>

        {/* Hero — Challenge of the Week */}
        <div className="relative overflow-hidden bg-gradient-to-br from-primary/15 via-card to-black border-2 border-primary/30 rounded-sm p-6 md:p-8">
          <div className="absolute top-0 right-0 w-40 h-40 bg-primary/10 rounded-full -mr-20 -mt-20 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 rounded-full px-3 py-1 mb-4">
              <span className="text-[10px] uppercase tracking-[0.3em] text-primary font-bold">⚡ Live This Week</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mb-3" data-testid="challenge-title">{ch.title}</h2>
            <p className="text-muted-foreground text-base mb-4 max-w-2xl">{ch.description}</p>
            
            {ch.instructions && (
              <div className="bg-black/40 border border-border/40 rounded-sm p-4 mb-4">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1">
                  <Info size={12} /> Instructions
                </p>
                <p className="text-sm whitespace-pre-wrap">{ch.instructions}</p>
              </div>
            )}

            {/* Demo video */}
            {ch.demo_video_url && (() => {
              const isInternal = ch.demo_video_url.includes('/api/challenges/video/');
              const isYouTube = /youtube\.com|youtu\.be/i.test(ch.demo_video_url);
              const handleClick = (e) => {
                if (isInternal) {
                  e.preventDefault();
                  setDemoVideoOpen(true);
                }
                // else default <a> behavior opens external (YouTube etc) in new tab
              };
              // Thumbnail card if we have one — much higher CTR than a text link
              if (ch.demo_thumbnail_url) {
                return (
                  <a
                    href={ch.demo_video_url}
                    onClick={handleClick}
                    target={isInternal ? undefined : '_blank'}
                    rel="noreferrer"
                    className="group relative block w-full max-w-sm aspect-video rounded-sm overflow-hidden border border-border hover:border-primary/60 transition mb-6"
                    data-testid="demo-video-thumbnail"
                  >
                    <img
                      src={ch.demo_thumbnail_url}
                      alt="Demo video preview"
                      className="w-full h-full object-cover transition group-hover:scale-[1.02]"
                    />
                    <div className="absolute inset-0 bg-black/35 group-hover:bg-black/20 transition flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-primary/90 text-black flex items-center justify-center shadow-lg">
                        <Play size={26} fill="currentColor" />
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                      <p className="text-[10px] uppercase tracking-widest text-primary">
                        {isYouTube ? 'Watch on YouTube' : 'Watch demo'}
                      </p>
                    </div>
                  </a>
                );
              }
              // Fallback: existing text link (YouTube + no thumb scenarios)
              return (
                <a
                  href={ch.demo_video_url}
                  onClick={handleClick}
                  target={isInternal ? undefined : '_blank'}
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-6"
                  data-testid="demo-video-link"
                >
                  <Play size={14} /> Watch demo video
                </a>
              );
            })()}

            {/* My status */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-black/40 border border-border/40 rounded-sm p-3">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Your Score</p>
                <p className="font-heading text-2xl text-primary mt-1" data-testid="my-best-score">
                  {myAth?.best_score != null ? (ch.scoring_type === 'time' ? `${myAth.best_score}s` : myAth.best_score) : '—'}
                </p>
              </div>
              <div className="bg-black/40 border border-border/40 rounded-sm p-3">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Rank</p>
                <p className="font-heading text-2xl text-primary mt-1" data-testid="my-rank">
                  {myAth?.rank ? `#${myAth.rank}` : '—'}
                </p>
              </div>
              <div className="bg-black/40 border border-border/40 rounded-sm p-3">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Streak</p>
                <p className="font-heading text-2xl text-primary mt-1 flex items-center gap-1" data-testid="my-streak">
                  {myAth?.streak >= 12 ? '🏅' : myAth?.streak >= 8 ? '⚡' : myAth?.streak >= 4 ? '🔥' : ''} {myAth?.streak || 0}
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => setView('rules')}
                className="bg-primary text-black hover:bg-primary/90 uppercase tracking-wider font-bold h-12 px-6"
                data-testid="accept-challenge-btn"
              >
                <Camera size={18} className="mr-2" />
                {myAth?.attempts_count > 0 ? 'Try Again' : 'Accept Challenge'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setView('ranking')}
                className="border-primary/40 text-primary hover:bg-primary/10 uppercase tracking-wider h-12"
                data-testid="view-ranking-btn"
              >
                <Users size={16} className="mr-2" />
                Ranking
              </Button>
            </div>
          </div>
        </div>

        {/* Parent share nudge */}
        {myAth?.best_score != null && (
          <div className="bg-gradient-to-r from-emerald-500/10 to-primary/10 border border-emerald-500/30 rounded-sm p-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm font-medium text-emerald-300">
                {myAth?.rank && myAth.rank <= 10 ? `🔥 ${athlete?.name || 'Your player'} is in the Top 10!` : `${athlete?.name || 'Your player'} is on the board.`}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">Share the video or the card — every share counts.</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {myAth?.best_branded_video_url && (
                <Button
                  size="sm"
                  onClick={() => setVideoShareOpen(true)}
                  className="bg-primary text-black hover:bg-primary/90 uppercase tracking-wider font-bold"
                  data-testid="parent-share-video-btn"
                >
                  <Video size={14} className="mr-2" /> Share Video
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCardOpen(true)}
                className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 uppercase tracking-wider font-bold"
                data-testid="parent-share-btn"
              >
                <Share2 size={14} className="mr-2" /> Share Card
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Rules modal */}
      {view === 'rules' && (
        <ChallengeRulesModal onAccept={() => setView('upload')} onClose={() => setView('home')} />
      )}

      {/* Share card modal */}
      {cardOpen && (
        <ShareableCard
          challenge={ch}
          athlete={{ ...athlete, name: myAth?.athlete_name || athlete?.name }}
          rank={myAth?.rank}
          score={myAth?.best_score}
          totalParticipants={data.total_participants}
          onClose={() => setCardOpen(false)}
        />
      )}

      {/* Share video modal */}
      <VideoShareModal
        open={videoShareOpen}
        onClose={() => setVideoShareOpen(false)}
        attempt={{
          athlete_id: myAth?.athlete_id,
          score: myAth?.best_score,
          video_url: myAth?.best_video_url,
          branded_video_url: myAth?.best_branded_video_url,
        }}
        challenge={ch}
        athlete={{ id: myAth?.athlete_id, name: myAth?.athlete_name || athlete?.name }}
        rank={myAth?.rank}
      />

      {/* Demo video inline player (storage-hosted demos) */}
      {demoVideoOpen && ch?.demo_video_url && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setDemoVideoOpen(false)}
          data-testid="demo-video-modal"
        >
          <button
            onClick={() => setDemoVideoOpen(false)}
            className="absolute top-4 right-4 text-white hover:text-primary"
            data-testid="demo-video-close"
          >
            <X size={28} />
          </button>
          <div
            className="w-full max-w-3xl aspect-video bg-black rounded-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              src={ch.demo_video_url}
              controls
              autoPlay
              playsInline
              className="w-full h-full"
              data-testid="demo-video-player"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default WeeklyChallenge;
